import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useContentfulWebPage } from 'marketing/api'
import styled from 'styled-components'
import { rem } from 'polished'
import Components from './Components'
import HttpStatus from 'components/Common/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { useAppSelector } from 'hooks/reduxHooks'
import HeadData from 'components/Common/HeadData'
import GeoContext from 'contexts/geoContext'
import ContentfulModal from './Modal/ContentfulModal'
import ModalContext from 'contexts/ModalContext'
import { useLocation } from 'react-router-dom'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import useQueryParams from 'hooks/useQueryParams'
import GolfMerchandiseCategoryCarousel from './GolfMerchandiseCategoryCarousel'
import { useIntl } from 'react-intl'
import UrlLocation from 'src/lib/url/UrlLocation'

const GOLF_CAMPAIGN_PATH_SUFFIX = '/campaign/golf'

function isOfferListCarousel(component: App.ContentfulWebPageComponent): component is App.ContentfulWebPageOfferCarousel {
  return component.contentTypeId === 'componentExperienceOfferListCarousel'
}

const ContentfulWebPageContainer = styled(Pane)`
  padding-bottom: ${rem(40)};
`

interface Props {
  topChildren?: React.ReactNode
  bottomChildren?: React.ReactNode
}

function ContentfulWebPage(props: Props) {
  const intl = useIntl()
  const { currentRegionCode, locale } = useContext(GeoContext)
  const pathname = useAppSelector((state) => state.router.location.pathname)
  // Contentful slugs omit both region and locale, e.g. /au/de/campaign/foo -> /campaign/foo.
  const contentfulPath = useMemo(() => new UrlLocation(pathname).withoutLocale().path.slice(3), [pathname])
  const { data: pageData, isLoading } = useContentfulWebPage(contentfulPath, currentRegionCode, locale)
  const showModal = useContext(ModalContext)
  const location = useLocation()
  const navigation = useAppNavigation()
  const query = useQueryParams()
  const activeModalSlug = query.get('open_modal')

  const shouldInsertGolfCarousel = useMemo(() => {
    return pathname.toLowerCase().endsWith(GOLF_CAMPAIGN_PATH_SUFFIX)
  }, [pathname])
  const golfInsertAfterTitle = intl.formatMessage({
    id: 'H3-ZVyyQi_sXKbsARprt',
    defaultMessage: 'World-Class Courses, Expertly Curated',
    description: 'Contentful section title used to insert the golf merchandise carousel on the golf campaign page',
  })
  const isRegionAllowed = useMemo(() => {
    if (!pageData) {
      return true
    }

    return !pageData.allowedRegions.length || pageData.allowedRegions.includes(currentRegionCode.toUpperCase())
  }, [currentRegionCode, pageData])

  const openModal = useCallback(async() => {
    const params = new URLSearchParams(location.search)
    params.delete('open_modal')
    const newSearch = params.toString()
    navigation.replace(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`)
    await showModal(<ContentfulModal contentModals={pageData?.modalComponents} slug={activeModalSlug} />)
  }, [activeModalSlug, location, navigation, pageData, showModal])

  useEffect(() => {
    if (activeModalSlug) {
      openModal()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModalSlug])

  const contentfulComponents = useMemo(() => {
    if (!pageData?.components) {
      return []
    }

    return pageData.components.reduce<Array<React.ReactNode>>((elements, component, index) => {
      const componentKey = `${component.contentTypeId}-${index}`
      const componentElement = <div
        key={componentKey}
        id={componentKey}
        data-type={component.contentTypeId}
      >
        <Components {...component} />
      </div>

      elements.push(componentElement)

      if (shouldInsertGolfCarousel && isOfferListCarousel(component) && component.title === golfInsertAfterTitle) {
        elements.push(<GolfMerchandiseCategoryCarousel key="golf-merchandise-carousel" />)
      }

      return elements
    }, [])
  }, [golfInsertAfterTitle, pageData, shouldInsertGolfCarousel])

  if (isLoading) {
    return <LoadingIndicator />
  }
  if (!pageData) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }
  if (!isRegionAllowed) {
    return <HttpStatus code={404}>
      <Error404Page invalidRegion />
    </HttpStatus>
  }

  return (
    <>
      <HeadData title={pageData.seoTitle ? pageData.seoTitle : pageData.title} description={pageData.seoDescription} />
      <ContentfulWebPageContainer>
        {props.topChildren}
        {contentfulComponents}
        {props.bottomChildren}
      </ContentfulWebPageContainer>
    </>
  )
}

export default ContentfulWebPage
